import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { setNetworkEventsActiveCountSuccess } from '@redux/slices/networkAlertsSlice';
import { useAxiosAuth0 } from '@shared/config/useAxiosAuth0';
import {
    GenericTable,
    GenericTableColumn,
} from '@ui-components/Table/GenericTable';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import GenericDropdown from '@ui-components/Dropdown/GenericDropdown';
import GenericSearchbar from '@ui-components/Searchbar/GenericSearchbar';
import GenericDateTimeRangePicker from '@ui-components/DateTime/GenericDateTimeRangePicker';
import GenericSwitch from '@ui-components/Switch/GenericSwitch';
import { GenericButton } from '@ui-components/Button/GenericButton';
import GenericCard from '@ui-components/Card/GenericCard';
import GenericTabs from '@ui-components/Tabs/GenericTabs';
import EmptyItems from '@shared/components/EmptyItems';
import AlertingPausedNotice from './AlertingPausedNotice';
import { ReactComponent as IconEye } from '@assets/Icons/Icon_Eye.svg';
import ErrorFetchingItems from '@shared/components/ErrorFetching';
import { fetchNetworkEvents, fetchNetworkEventsCount, fetchNetworkIncidents, parseNetworkEventsActiveCount } from './requests/request';
import IncidentDetailModal from './IncidentDetailModal';
import {
    ALERT_SOUND_OPTIONS,
    AlertSoundOption,
    AlertSoundVariant,
    getSelectedAlertSoundVariant,
    getUnlockedEasterEggs,
    isAlertSoundMuted,
    playAlertSound,
    previewAlertSound,
    setAlertSoundMuted,
    setSelectedAlertSoundVariant,
    unlockEasterEgg,
} from '@shared/utils/playAlertSound';
import {
    AffectedService,
    EventStatus,
    IncidentGroup,
    NetworkEventRow,
    NetworkEventsResponse,
    formatDuration,
    formatTimestamp,
    getEventTypeLabel,
    getServiceDetailPath,
    getServiceTypeLabel,
} from './networkAlertTypes';

type ActiveTab = 'active' | 'history';
type EventTypeFilter =
    | ''
    | 'bgp_adjchange'
    | 'bfd_session'
    | 'interface_state_change'
    | 'zero_traffic'
    | 'traffic_resumed'
    | 'router_unreachable'
    | 'router_reachable';
type StatusFilter = '' | 'active' | 'resolved';
/** Which toolbar control initiated the current API filter fetch (for per-control loaders). */
type AlertsToolbarFilterKey = 'eventType' | 'status' | 'dateRange';

const INCIDENTS_PER_PAGE = 10;
const ACTIVE_ALARMS_LIMIT = 200;
/** Default rows per page for the active alarms table (client-side pagination). */
const ACTIVE_ALARMS_PER_PAGE_DEFAULT = 10;
const ACTIVE_ALARMS_ROWS_PER_PAGE_OPTIONS: number[] = [5, 10, 25];

/** GenericTable loading skeleton: shared by Active + History tables on this page. */
const NETWORK_ALERTS_TABLE_SKELETON_ROWS = 2;
const NETWORK_ALERTS_TABLE_SKELETON_COLS = 5;

const DURATION_REFRESH_MS = 60_000;
const AUTO_REFRESH_MS = 60_000;
/** Duration for "Updated" label fading from success styling back to default. */
const UPDATED_LABEL_TRANSITION_MS = 1600;
// Bump the version suffix to reset the persisted preference when the
// default behavior changes. v2 -> default ON, 1-minute interval.
const AUTO_REFRESH_STORAGE_KEY = 'ke.networkAlerts.autoRefresh.v2';
const AUTO_REFRESH_LEGACY_KEYS = ['ke.networkAlerts.autoRefresh'];

function readAutoRefreshPreference(): boolean {
    try {
        for (const legacy of AUTO_REFRESH_LEGACY_KEYS) {
            localStorage.removeItem(legacy);
        }
        const raw = localStorage.getItem(AUTO_REFRESH_STORAGE_KEY);
        if (raw === 'false') return false;
        return true;
    } catch {
        return true;
    }
}

function writeAutoRefreshPreference(value: boolean): void {
    try {
        localStorage.setItem(AUTO_REFRESH_STORAGE_KEY, String(value));
    } catch {
        /* ignore storage errors */
    }
}

/**
 * Invokes `callback` every `intervalMs` while `enabled` is true and the
 * document is visible. Pauses automatically when the browser tab is
 * hidden and resumes on visibility change. The latest `callback` ref
 * is always used so interval consumers do not need to memoize.
 */
function useAutoRefresh(
    callback: () => void,
    enabled: boolean,
    intervalMs: number
): void {
    const savedCb = useRef(callback);
    useEffect(() => {
        savedCb.current = callback;
    }, [callback]);

    useEffect(() => {
        if (!enabled) return undefined;
        const tick = () => {
            if (typeof document === 'undefined' || document.visibilityState === 'visible') {
                savedCb.current();
            }
        };
        const id = setInterval(tick, intervalMs);
        return () => clearInterval(id);
    }, [enabled, intervalMs]);
}

function formatClockTime(ts: number | null): string {
    if (!ts) return '--:--:--';
    const d = new Date(ts);
    return d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
}

// Watcher-side event types are gated by environment: visible in
// staging/dev for testing, hidden in production until the end-to-end
// pipeline (validate.js, ingest correlations, ENUM migrations) is fully
// re-enabled there. The entries in EVENT_TYPE_VALUE_MAP /
// EVENT_TYPE_LABEL_MAP stay populated regardless so historical rows
// still resolve a label even when the dropdown doesn't offer the filter.
// "Recovered" (traffic_resumed) is intentionally omitted: a recovery is a
// follow-up that closes a Data Rate warning, not an alarm category users
// would filter for. The eventType label still resolves via
// EVENT_TYPE_LABEL_MAP for any historical row.
const EVENT_TYPE_OPTIONS = [
    'All Events',
    'BGP',
    'BFD',
    'Interface State',
    'Data Rate',
    'Outage',
];
const EVENT_TYPE_VALUE_MAP: Record<string, EventTypeFilter> = {
    'All Events': '',
    'BGP': 'bgp_adjchange',
    'BFD': 'bfd_session',
    'Interface State': 'interface_state_change',
    'Data Rate': 'zero_traffic',
    // The "Outage" filter targets the alarm side (router_unreachable);
    // the matching router_reachable shows up via "All Events" and inside
    // the incident timeline once paired.
    'Outage': 'router_unreachable',
};
// Inverse map: kept exhaustive (including traffic_resumed → "Recovered")
// so the chip rendered on the active filter pill still resolves a label
// for any backend value, even when the dropdown no longer offers it.
const EVENT_TYPE_LABEL_MAP: Record<string, string> = {
    '': 'All Events',
    'bgp_adjchange': 'BGP',
    'bfd_session': 'BFD',
    'interface_state_change': 'Interface State',
    'zero_traffic': 'Data Rate',
    'traffic_resumed': 'Recovered',
    'router_unreachable': 'Outage',
    'router_reachable': 'Outage',
};

const STATUS_OPTIONS = ['All Statuses', 'Down', 'Resolved / Up'];
const STATUS_VALUE_MAP: Record<string, StatusFilter> = {
    'All Statuses': '',
    'Down': 'active',
    'Resolved / Up': 'resolved',
};
const STATUS_LABEL_MAP: Record<string, string> = {
    '': 'All Statuses',
    'active': 'Down',
    'resolved': 'Resolved / Up',
};

const STATUS_TAG_VARIANT: Record<EventStatus, 'error' | 'success' | 'warning'> = {
    'Down': 'error',
    'Resolved': 'success',
    'Up': 'success',
    'Warning': 'warning',
};

const ALERTS_TAB_QUERY = 'tab';
const DEFAULT_ALERTS_TAB = 'active';
const ALERTS_VALID_TAB_VALUES = new Set<string>(['active', 'history']);

function resolveIsActiveParam(filter: StatusFilter): string | undefined {
    if (filter === 'active') return 'true';
    if (filter === 'resolved') return 'false';
    return undefined;
}

/** Active Alarms (`GET /network-events`): always send `isActive` so the backend filters; default is active-only. */
function resolveNetworkEventsIsActiveParam(filter: StatusFilter): string {
    return filter === 'resolved' ? 'false' : 'true';
}


function buildServiceTooltip(
    svc: { serviceName: string; subscriptionId: string; serviceType: string },
    vrf?: string
): string {
    const parts = [getServiceTypeLabel(svc.serviceType)];
    if (vrf) parts.push('VRF: ' + vrf);
    parts.push(svc.subscriptionId);
    return parts.join(' \u2014 ');
}

function getIncidentNeighbors(incident: IncidentGroup): string[] {
    const set = new Set<string>();
    for (const evt of incident.events) {
        const ip = evt.detail?.neighbor || evt.detail?.peer;
        if (ip) set.add(ip);
    }
    return Array.from(set);
}

function incidentMatchesSearch(incident: IncidentGroup, term: string): boolean {
    if (!term) return true;
    const lower = term.toLowerCase();

    if (formatTimestamp(incident.startedAt).toLowerCase().includes(lower)) return true;

    for (const et of incident.eventTypes) {
        if (getEventTypeLabel(et).toLowerCase().includes(lower)) return true;
    }

    if (incident.status.toLowerCase().includes(lower)) return true;

    if (String(incident.eventCount).includes(lower)) return true;

    const start = new Date(incident.startedAt).getTime();
    const end = incident.resolvedAt ? new Date(incident.resolvedAt).getTime() : Date.now();
    if (formatDuration(Math.max(0, end - start)).toLowerCase().includes(lower)) return true;

    for (const evt of incident.events) {
        const ip = evt.detail?.neighbor || evt.detail?.peer || '';
        if (ip.toLowerCase().includes(lower)) return true;
    }

    for (const svc of incident.affectedServices) {
        if (svc.subscriptionId.toLowerCase().includes(lower)) return true;
        if (svc.serviceName?.toLowerCase().includes(lower)) return true;
        if (svc.customerName?.toLowerCase().includes(lower)) return true;
        if (svc.tags?.some((tag) => tag.toLowerCase().includes(lower))) return true;
    }

    return false;
}

type ActiveAlarmTableRow = {
    rowId: string;
    event: NetworkEventRow;
    services: AffectedService[];
};

/**
 * One table row per event. The per-service columns (Service type, ID,
 * Name, Customer) render stacked lists when an event affects more than
 * one service — common for parent-interface or router-level outages.
 *
 * The previous expansion (1 row per service) made the visible count
 * disagree with the API's `count` and the header badge, which is what
 * `Total : N` tracks. Keeping events 1:1 with rows resolves the mismatch.
 */
function expandEventsToTableRows(events: NetworkEventRow[]): ActiveAlarmTableRow[] {
    return events.map((event) => ({
        rowId: event.id,
        event,
        services: event.affectedServices ?? [],
    }));
}

function activeAlarmRowMatchesSearch(row: ActiveAlarmTableRow, term: string): boolean {
    if (!term) return true;
    const lower = term.toLowerCase();
    const ev = row.event;
    if (formatTimestamp(ev.timestamp).toLowerCase().includes(lower)) return true;
    if (getEventTypeLabel(ev.eventType).toLowerCase().includes(lower)) return true;
    if (ev.status.toLowerCase().includes(lower)) return true;
    if (ev.reason?.toLowerCase().includes(lower)) return true;
    const neighbor = ev.detail?.neighbor || ev.detail?.peer || '';
    if (neighbor.toLowerCase().includes(lower)) return true;
    if (ev.detail?.afiSafi?.toLowerCase().includes(lower)) return true;
    if (ev.detail?.vrf?.toLowerCase().includes(lower)) return true;
    const start = new Date(ev.timestamp).getTime();
    const endMs = ev.resolvedAt ? new Date(ev.resolvedAt).getTime() : Date.now();
    if (formatDuration(Math.max(0, endMs - start)).toLowerCase().includes(lower)) return true;
    for (const svc of row.services) {
        if (svc.subscriptionId.toLowerCase().includes(lower)) return true;
        if (svc.serviceName?.toLowerCase().includes(lower)) return true;
        if (svc.customerName?.toLowerCase().includes(lower)) return true;
        if (svc.tags?.some((tag) => tag.toLowerCase().includes(lower))) return true;
        if (getServiceTypeLabel(svc.serviceType).toLowerCase().includes(lower)) return true;
    }
    return false;
}

/* ---------- Active Alarms Section ---------- */

const ActiveAlarmsSection: React.FC<{
    tenantId: number;
    autoRefresh: boolean;
    onLastUpdatedChange: (ts: number) => void;
    onActiveEventTotalChange?: (total: number) => void;
    onSuccessfulRefresh?: () => void;
    onFetchingChange: (isFetching: boolean) => void;
    refreshNonce: number;
    syncAlertsMenuNetworkEventsCount: () => Promise<void>;
}> = ({
    tenantId,
    autoRefresh,
    onLastUpdatedChange,
    onActiveEventTotalChange,
    onSuccessfulRefresh,
    onFetchingChange,
    refreshNonce,
    syncAlertsMenuNetworkEventsCount,
}) => {
    const api = useAxiosAuth0();
    const navigate = useNavigate();
    const [alarms, setAlarms] = useState<NetworkEventRow[]>([]);
    const [eventTotal, setEventTotal] = useState(0);
    const [isFetching, setIsFetching] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [hasFetched, setHasFetched] = useState(false);
    const [now, setNow] = useState(Date.now());
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // Tracks the last successful event total so the auto-refresh can
    // ping the user with a sound when the count increases between polls.
    // Reset on tenant/filter change so a different filter set doesn't
    // trigger a spurious sound just because it returns more rows.
    const previousActiveTotalRef = useRef<number | null>(null);

    const [eventTypeFilter, setEventTypeFilter] = useState<EventTypeFilter>('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
    const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({
        start: null,
        end: null,
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [filterResetKey, setFilterResetKey] = useState(0);
    const [activeFilterLoadingKey, setActiveFilterLoadingKey] = useState<AlertsToolbarFilterKey | null>(null);

    const hasApiFilters = Boolean(
        eventTypeFilter || statusFilter || dateRange.start || dateRange.end
    );
    const hasActiveAnyFilter = Boolean(hasApiFilters || searchTerm);

    const handleClearFilters = useCallback(() => {
        setActiveFilterLoadingKey(null);
        setEventTypeFilter('');
        setStatusFilter('');
        setSearchTerm('');
        setDateRange({ start: null, end: null });
        setFilterResetKey((k) => k + 1);
    }, []);

    const handleServiceClick = useCallback(
        (serviceType: string, serviceId: number | string) => {
            const path = getServiceDetailPath(serviceType, serviceId);
            if (path) navigate(path);
        },
        [navigate]
    );

    const tableRows = useMemo(() => expandEventsToTableRows(alarms), [alarms]);

    const filteredTableRows = useMemo(
        () => tableRows.filter((row) => activeAlarmRowMatchesSearch(row, searchTerm)),
        [tableRows, searchTerm]
    );

    const activeAlarmColumns = useMemo<GenericTableColumn<ActiveAlarmTableRow>[]>(() => {
        const em = '\u2014';
        return [
            {
                header: 'Event type',
                accessor: 'event' as keyof ActiveAlarmTableRow,
                sortAccessor: (row) => row.event.eventType,
                renderCell: (row) => (
                    <GenericTag
                        label={getEventTypeLabel(row.event.eventType)}
                        variant="neutral"
                        size="small"
                        rounded="medium"
                    />
                ),
            },
            {
                header: 'Service type',
                accessor: 'services' as keyof ActiveAlarmTableRow,
                sortAccessor: (row) => row.services[0]?.serviceType ?? '',
                renderCell: (row) =>
                    row.services.length === 0 ? (
                        <span className="text-xs text-semantic-text-secondary">{em}</span>
                    ) : (
                        <div className="flex flex-col gap-0.5">
                            {row.services.map((svc) => (
                                <span
                                    key={`${svc.serviceType}:${svc.serviceId}`}
                                    className="text-xs text-semantic-text-primary"
                                >
                                    {getServiceTypeLabel(svc.serviceType)}
                                </span>
                            ))}
                        </div>
                    ),
            },
            {
                header: 'Service ID',
                accessor: 'services' as keyof ActiveAlarmTableRow,
                sortAccessor: (row) => row.services[0]?.subscriptionId ?? '',
                wrapMode: 'break',
                renderCell: (row) => {
                    if (row.services.length === 0) {
                        return <span className="text-xs text-semantic-text-secondary">{em}</span>;
                    }
                    return (
                        <div className="flex flex-col gap-0.5">
                            {row.services.map((svc) => {
                                const detailPath = getServiceDetailPath(svc.serviceType, svc.serviceId);
                                const tooltip = buildServiceTooltip(svc, row.event.detail?.vrf);
                                const key = `${svc.serviceType}:${svc.serviceId}`;
                                if (detailPath) {
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            {...viewHref(detailPath)}
                                            className="text-left text-xs font-mono font-bold text-semantic-text-brand underline decoration-semantic-border-secondary hover:opacity-90"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleServiceClick(svc.serviceType, svc.serviceId);
                                            }}
                                            title={tooltip}
                                        >
                                            {svc.subscriptionId}
                                        </button>
                                    );
                                }
                                return (
                                    <span
                                        key={key}
                                        className="text-xs font-mono font-bold text-semantic-text-brand"
                                        title={tooltip}
                                    >
                                        {svc.subscriptionId}
                                    </span>
                                );
                            })}
                        </div>
                    );
                },
            },
            {
                header: 'Service name',
                accessor: 'services' as keyof ActiveAlarmTableRow,
                sortAccessor: (row) => row.services[0]?.serviceName ?? '',
                wrapMode: 'break',
                renderCell: (row) =>
                    row.services.length === 0 ? (
                        <span className="text-xs text-semantic-text-secondary">{em}</span>
                    ) : (
                        <div className="flex flex-col gap-0.5">
                            {row.services.map((svc) => (
                                <span
                                    key={`${svc.serviceType}:${svc.serviceId}`}
                                    className="text-xs text-semantic-text-primary"
                                >
                                    {svc.serviceName || em}
                                </span>
                            ))}
                        </div>
                    ),
            },
            {
                header: 'Customer',
                accessor: 'services' as keyof ActiveAlarmTableRow,
                sortAccessor: (row) => row.services[0]?.customerName ?? '',
                wrapMode: 'break',
                renderCell: (row) => {
                    if (row.services.length === 0) {
                        return <span className="text-xs text-semantic-text-secondary">{em}</span>;
                    }
                    // Collapse repeated customer names — common case is a
                    // single customer covering all services on the event.
                    const seen = new Set<string>();
                    const customers: string[] = [];
                    for (const svc of row.services) {
                        const name = svc.customerName || '';
                        if (!name || seen.has(name)) continue;
                        seen.add(name);
                        customers.push(name);
                    }
                    if (customers.length === 0) {
                        return <span className="text-xs text-semantic-text-secondary">{em}</span>;
                    }
                    return (
                        <div className="flex flex-col gap-0.5">
                            {customers.map((name) => (
                                <span
                                    key={name}
                                    className="text-xs font-medium text-semantic-text-primary"
                                >
                                    {name}
                                </span>
                            ))}
                        </div>
                    );
                },
            },
            {
                header: 'Duration',
                accessor: 'event' as keyof ActiveAlarmTableRow,
                sortAccessor: (row) => {
                    const endMs = row.event.resolvedAt
                        ? new Date(row.event.resolvedAt).getTime()
                        : now;
                    const startMs = new Date(row.event.timestamp).getTime();
                    return Math.max(0, endMs - startMs);
                },
                renderCell: (row) => {
                    const endMs = row.event.resolvedAt
                        ? new Date(row.event.resolvedAt).getTime()
                        : now;
                    const startMs = new Date(row.event.timestamp).getTime();
                    return (
                        <span className="text-xs font-mono text-semantic-text-primary whitespace-nowrap">
                            {formatDuration(Math.max(0, endMs - startMs))}
                        </span>
                    );
                },
            },
            {
                header: 'Reason',
                accessor: 'event' as keyof ActiveAlarmTableRow,
                sortAccessor: (row) => row.event.reason ?? '',
                width: '240px',
                wrapMode: 'nowrap',
                renderCell: (row) =>
                    row.event.reason ? (
                        <div className="max-w-[240px] overflow-x-auto overflow-y-hidden custom-scrollbar pr-0.5">
                            <span className="text-xs text-semantic-text-primary whitespace-nowrap inline-block">
                                {row.event.reason}
                            </span>
                        </div>
                    ) : (
                        <span className="text-xs text-semantic-text-secondary">{em}</span>
                    ),
            },
            {
                header: 'Started',
                accessor: 'event' as keyof ActiveAlarmTableRow,
                sortAccessor: (row) => new Date(row.event.timestamp).getTime(),
                renderCell: (row) => (
                    <span className="text-xs font-mono whitespace-nowrap">
                        {formatTimestamp(row.event.timestamp)}
                    </span>
                ),
            },
            {
                header: 'Neighbor',
                accessor: 'event' as keyof ActiveAlarmTableRow,
                sortAccessor: (row) => row.event.detail?.neighbor || row.event.detail?.peer || '',
                renderCell: (row) => {
                    const ip = row.event.detail?.neighbor || row.event.detail?.peer;
                    return ip ? (
                        <span className="text-xs font-mono text-semantic-text-primary whitespace-nowrap">{ip}</span>
                    ) : (
                        <span className="text-xs text-semantic-text-secondary">{em}</span>
                    );
                },
            },
        ];
    }, [now, handleServiceClick]);

    useEffect(() => {
        onFetchingChange(isFetching);
    }, [isFetching, onFetchingChange]);

    useEffect(() => {
        onActiveEventTotalChange?.(eventTotal);
    }, [eventTotal, onActiveEventTotalChange]);

    const fetchActive = useCallback(async () => {
        try {
            setIsFetching(true);
            setFetchError(null);
            const response = await fetchNetworkEvents(api, {
                tenantId,
                eventType: eventTypeFilter || undefined,
                isActive: resolveNetworkEventsIsActiveParam(statusFilter),
                since: dateRange.start ? dateRange.start.toISOString() : undefined,
                until: dateRange.end ? dateRange.end.toISOString() : undefined,
                limit: ACTIVE_ALARMS_LIMIT,
                offset: 0,
                order: 'desc',
            });
            const { data } = response;
            if (data.success && data.data) {
                const payload = data.data as NetworkEventsResponse;
                const events = payload.events ?? [];
                setAlarms(events);
                // Count incidents, not event rows: that is the unit the
                // side-menu badge uses, and a single fault can carry
                // several active events (a BGP Down and the BFD Down of
                // the same link) which would otherwise make the tab
                // header read higher than the badge. Falls back to the
                // row total on the legacy path — a resolved listing sends
                // isActive=false, which the backend serves from the event
                // scan with no `incidents` block.
                const pag = payload.pagination;
                const rowTotal =
                    typeof pag?.total === 'number' ? pag.total : events.length;
                const incidentTotal = payload.incidents?.active;
                const newTotal =
                    typeof incidentTotal === 'number'
                        ? incidentTotal
                        : rowTotal;
                const prevTotal = previousActiveTotalRef.current;
                // The alert sound follows the same unit, so a flap that
                // opens two events on one incident chimes once.
                if (prevTotal !== null && newTotal > prevTotal) {
                    playAlertSound();
                }
                previousActiveTotalRef.current = newTotal;
                setEventTotal(newTotal);
                onLastUpdatedChange(Date.now());
                onSuccessfulRefresh?.();
            } else {
                setFetchError(data.message || 'Unknown error');
                setAlarms([]);
                setEventTotal(0);
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Network error';
            setFetchError(msg);
            setAlarms([]);
            setEventTotal(0);
        } finally {
            setIsFetching(false);
            setHasFetched(true);
            setActiveFilterLoadingKey(null);
        }
        await syncAlertsMenuNetworkEventsCount();
    }, [
        api,
        tenantId,
        onLastUpdatedChange,
        syncAlertsMenuNetworkEventsCount,
        eventTypeFilter,
        statusFilter,
        dateRange,
        onSuccessfulRefresh,
    ]);


    // Reset the sound baseline whenever tenant or filters change. The
    // first fetch after the change establishes a fresh baseline (no
    // sound), and subsequent auto-refreshes can compare against it.
    useEffect(() => {
        previousActiveTotalRef.current = null;
    }, [tenantId, eventTypeFilter, statusFilter, dateRange]);

    useEffect(() => {
        fetchActive();
    }, [fetchActive, refreshNonce]);

    useAutoRefresh(fetchActive, autoRefresh, AUTO_REFRESH_MS);

    useEffect(() => {
        intervalRef.current = setInterval(() => setNow(Date.now()), DURATION_REFRESH_MS);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, []);

    /** Table skeleton: first load, empty result, or refetch while still empty — not when rows exist (same-count refresh). */
    const showActiveTableSkeleton =
        !hasFetched || (isFetching && alarms.length === 0);

    const showEmptyNoAlarms =
        hasFetched &&
        !fetchError &&
        !showActiveTableSkeleton &&
        alarms.length === 0 &&
        !hasApiFilters;

    const showNoMatch =
        hasFetched &&
        !fetchError &&
        !showActiveTableSkeleton &&
        ((alarms.length === 0 && hasApiFilters) ||
            (alarms.length > 0 && filteredTableRows.length === 0));

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {!showEmptyNoAlarms && (
                <>
                    {/* Filters toolbar (same controls as History; query params match /network-events) */}
                    <div className="relative z-30 flex flex-wrap items-end gap-3 overflow-visible bg-semantic-bg-primary px-4 pb-3 pt-4">
                        <GenericDropdown
                            items={EVENT_TYPE_OPTIONS}
                            value={EVENT_TYPE_LABEL_MAP[eventTypeFilter] || 'All Events'}
                            onSelect={(label) => {
                                setActiveFilterLoadingKey('eventType');
                                setEventTypeFilter(EVENT_TYPE_VALUE_MAP[label] ?? '');
                            }}
                            size="sm"
                            widthClass="w-auto"
                            minWidthClass="min-w-[120px]"
                            maxWidthClass="max-w-[160px]"
                            disabled={isFetching}
                            loading={isFetching && activeFilterLoadingKey === 'eventType'}
                            fixedPosition
                        />
                        <GenericDropdown
                            items={STATUS_OPTIONS}
                            value={STATUS_LABEL_MAP[statusFilter] || 'All Statuses'}
                            onSelect={(label) => {
                                setActiveFilterLoadingKey('status');
                                setStatusFilter(STATUS_VALUE_MAP[label] ?? '');
                            }}
                            size="sm"
                            widthClass="w-auto"
                            minWidthClass="min-w-[120px]"
                            maxWidthClass="max-w-[180px]"
                            disabled={isFetching}
                            loading={isFetching && activeFilterLoadingKey === 'status'}
                            fixedPosition
                        />
                        <GenericDateTimeRangePicker
                            label=""
                            value={dateRange}
                            onChange={(val) => {
                                setActiveFilterLoadingKey('dateRange');
                                setDateRange(val);
                            }}
                            maxDate={new Date()}
                            dateOnly
                            className="w-[220px]"
                            disabled={isFetching}
                        />
                        <div className="min-w-[180px] max-w-[320px] flex-1">
                            <GenericSearchbar
                                key={filterResetKey}
                                placeholder="Search neighbor, service, customer..."
                                size="xs"
                                onFiltersChange={Function.prototype as () => void}
                                serverSideSearch
                                onServerSideSearchChange={(val) => setSearchTerm(val)}
                                serverSideDebounceMs={300}
                                inputMaxWidth="100%"
                            />
                        </div>
                    </div>

                    {hasActiveAnyFilter && (
                        <div className="px-4 pb-2">
                            <GenericButton
                                variant="outlined"
                                size="xsm"
                                onClick={handleClearFilters}
                                label="Clear filters"
                            />
                        </div>
                    )}
                </>
            )}

            {fetchError && <ErrorFetchingItems text={fetchError} />}

            {!fetchError && showEmptyNoAlarms && <EmptyItems />}

            {!fetchError && showNoMatch && (
                <div className="flex items-center justify-center py-12 text-sm text-semantic-text-secondary">
                    No events match the current filters.
                </div>
            )}

            {!fetchError && !showEmptyNoAlarms && !showNoMatch && (
                <GenericCard
                    hasBorder={false}
                    isCollapsible={false}
                    className="flex min-h-0 w-full min-w-0 flex-1 flex-col border-0 px-4 pb-4 shadow-none mt-4"
                >
                    <GenericTable<ActiveAlarmTableRow>
                        columns={activeAlarmColumns}
                        data={showActiveTableSkeleton ? [] : filteredTableRows}
                        rowKey={(row) => row.rowId}
                        initialRowsPerPage={ACTIVE_ALARMS_PER_PAGE_DEFAULT}
                        rowsPerPageOptions={ACTIVE_ALARMS_ROWS_PER_PAGE_OPTIONS}
                        loading={showActiveTableSkeleton}
                        loadingSkeletonRows={NETWORK_ALERTS_TABLE_SKELETON_ROWS}
                        loadingSkeletonCols={NETWORK_ALERTS_TABLE_SKELETON_COLS}
                        horizontalScroll="always"
                        width="100%"
                        tableClassName="bg-semantic-bg-primary"
                        maxTableBodyHeight="100%"
                    />
                </GenericCard>
            )}
        </div>
    );
};

/* ---------- Main Page ---------- */

const NetworkAlertsPage: React.FC = () => {
    const { tenantId, partnerId } = useAppSelector((state) => state.selection);
    const isViewingAllCustomers = tenantId === partnerId;
    const api = useAxiosAuth0();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const syncAlertsMenuNetworkEventsCount = useCallback(async () => {
        try {
            const countRes = await fetchNetworkEventsCount(api, { tenantId });
            const parsed = parseNetworkEventsActiveCount(countRes.data);
            if (parsed !== null) {
                dispatch(
                    setNetworkEventsActiveCountSuccess({ tenantId, active: parsed })
                );
            }
        } catch {
            /* keep last successful Redux count */
        }
    }, [api, tenantId, dispatch]);

    const handleServiceClick = useCallback(
        (serviceType: string, serviceId: number | string) => {
            const path = getServiceDetailPath(serviceType, serviceId);
            if (path) navigate(path);
        },
        [navigate]
    );

    const [searchParams, setSearchParams] = useSearchParams();
    const rawAlertsTab = searchParams.get(ALERTS_TAB_QUERY) || DEFAULT_ALERTS_TAB;
    const alertsTabValue = ALERTS_VALID_TAB_VALUES.has(rawAlertsTab)
        ? rawAlertsTab
        : DEFAULT_ALERTS_TAB;
    const activeTab = alertsTabValue as ActiveTab;

    const [activeAlarmsEventTotal, setActiveAlarmsEventTotal] = useState(0);
    const alertsPageTabs = useMemo(
        () => [
            { label: `Active Alarms (${activeAlarmsEventTotal})`, value: 'active' },
            { label: 'History', value: 'history' },
        ],
        [activeAlarmsEventTotal]
    );

    useEffect(() => {
        setActiveAlarmsEventTotal(0);
    }, [tenantId]);

    /** History tab fetch state (distinct name from ActiveAlarmsSection's `isFetching` to avoid TDZ / tooling confusion). */
    const [historyFetching, setHistoryFetching] = useState(false);
    const prevAlertsTabRef = useRef<ActiveTab>(activeTab);
    const [historySwitchSkeleton, setHistorySwitchSkeleton] = useState(false);

    const handleAlertsTabChange = useCallback(
        (value: string) => {
            if (value === 'history' && alertsTabValue === 'active') {
                setHistorySwitchSkeleton(true);
            }
            if (value === 'active' && alertsTabValue === 'history') {
                setHistorySwitchSkeleton(false);
            }
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set(ALERTS_TAB_QUERY, value);
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams, alertsTabValue]
    );

    const [autoRefresh, setAutoRefresh] = useState<boolean>(readAutoRefreshPreference);
    const [activeLastUpdated, setActiveLastUpdated] = useState<number | null>(null);
    const [historyLastUpdated, setHistoryLastUpdated] = useState<number | null>(null);
    const [activeRefreshNonce, setActiveRefreshNonce] = useState(0);
    const [activeIsFetching, setActiveIsFetching] = useState(false);
    const [updatedLabelFlash, setUpdatedLabelFlash] = useState(false);
    const updatedLabelFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [autoRefreshMenuOpen, setAutoRefreshMenuOpen] = useState(false);
    const autoRefreshMenuRef = useRef<HTMLDivElement | null>(null);
    const [soundMenuOpen, setSoundMenuOpen] = useState(false);
    const soundMenuRef = useRef<HTMLDivElement | null>(null);
    const [soundVariant, setSoundVariant] = useState<AlertSoundVariant>(() =>
        getSelectedAlertSoundVariant()
    );
    const [soundMuted, setSoundMuted] = useState<boolean>(() => isAlertSoundMuted());
    const [unlockedEggs, setUnlockedEggs] = useState<AlertSoundOption[]>(() =>
        getUnlockedEasterEggs()
    );

    const handleSelectSoundVariant = useCallback((v: AlertSoundVariant) => {
        setSelectedAlertSoundVariant(v);
        setSoundVariant(v);
        previewAlertSound(v);
    }, []);

    const handleToggleSoundMuted = useCallback(() => {
        const next = !isAlertSoundMuted();
        setAlertSoundMuted(next);
        setSoundMuted(next);
    }, []);

    const triggerUpdatedLabelFlash = useCallback(() => {
        if (updatedLabelFlashTimerRef.current) {
            clearTimeout(updatedLabelFlashTimerRef.current);
            updatedLabelFlashTimerRef.current = null;
        }
        setUpdatedLabelFlash(true);
        updatedLabelFlashTimerRef.current = setTimeout(() => {
            setUpdatedLabelFlash(false);
            updatedLabelFlashTimerRef.current = null;
        }, 50);
    }, []);

    useEffect(() => {
        return () => {
            if (updatedLabelFlashTimerRef.current) {
                clearTimeout(updatedLabelFlashTimerRef.current);
            }
        };
    }, []);
    const [detailIncident, setDetailIncident] = useState<IncidentGroup | null>(null);

    const handleAutoRefreshChange = useCallback((next: boolean) => {
        setAutoRefresh(next);
        writeAutoRefreshPreference(next);
    }, []);

    /* -- History state -- */
    const [incidents, setIncidents] = useState<IncidentGroup[]>([]);
    const [totalIncidents, setTotalIncidents] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [cursorStack, setCursorStack] = useState<string[]>([]);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [hasFetched, setHasFetched] = useState(false);

    useEffect(() => {
        if (prevAlertsTabRef.current === 'active' && activeTab === 'history') {
            setHistorySwitchSkeleton(true);
        }
        prevAlertsTabRef.current = activeTab;
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 'history' && !historyFetching) {
            setHistorySwitchSkeleton(false);
        }
    }, [activeTab, historyFetching]);

    const [eventTypeFilter, setEventTypeFilter] = useState<EventTypeFilter>('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
    const [filterResetKey, setFilterResetKey] = useState(0);
    const [historyFilterLoadingKey, setHistoryFilterLoadingKey] = useState<AlertsToolbarFilterKey | null>(null);

    const currentCursor = cursorStack.length > 0 ? cursorStack.at(-1) : undefined;

    const fetchData = useCallback(async (cursor?: string) => {
        try {
            setHistoryFetching(true);
            setFetchError(null);
            const response = await fetchNetworkIncidents(api, {
                tenantId,
                limit: INCIDENTS_PER_PAGE,
                cursor,
                eventType: eventTypeFilter || undefined,
                isActive: resolveIsActiveParam(statusFilter),
                since: dateRange.start ? dateRange.start.toISOString() : undefined,
                until: dateRange.end ? dateRange.end.toISOString() : undefined,
                order: 'desc',
            });
            const { data } = response;
            if (data.success && data.data) {
                setIncidents(data.data.incidents);
                setHasMore(data.data.pagination.hasMore);
                setTotalIncidents(data.data.pagination.totalIncidents);
                setHistoryLastUpdated(Date.now());
                triggerUpdatedLabelFlash();
            } else {
                setFetchError(data.message || 'Unknown error');
                setIncidents([]);
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Network error';
            setFetchError(msg);
            setIncidents([]);
        } finally {
            setHistoryFetching(false);
            setHasFetched(true);
            setHistoryFilterLoadingKey(null);
        }
        await syncAlertsMenuNetworkEventsCount();
    }, [
        api,
        tenantId,
        eventTypeFilter,
        statusFilter,
        dateRange,
        syncAlertsMenuNetworkEventsCount,
        triggerUpdatedLabelFlash,
    ]);

    useEffect(() => {
        if (activeTab === 'history') fetchData(currentCursor);
    }, [activeTab, fetchData, currentCursor]);

    const refreshHistory = useCallback(() => {
        fetchData(currentCursor);
    }, [fetchData, currentCursor]);

    useAutoRefresh(
        refreshHistory,
        autoRefresh && activeTab === 'history',
        AUTO_REFRESH_MS
    );

    const handleManualRefresh = useCallback(() => {
        if (activeTab === 'active') {
            setActiveRefreshNonce((n) => n + 1);
        } else {
            refreshHistory();
        }
    }, [activeTab, refreshHistory]);

    // Easter eggs: hidden secret variants unlocked via key combos.
    //   Konami code (↑↑↓↓←→←→BA) → Imperial March
    //   Type "eva"               → Cruel Angel's Thesis
    //   Type "rick"              → Rickroll (8-bit)
    //   Type "aot"               → Attack on Titan (Guren no Yumiya)
    // First trigger unlocks the variant and persists it in localStorage;
    // subsequent triggers just play it. Once unlocked the variant shows
    // up in the Sound dropdown so the user can pick it as the active
    // notification sound. Suppressed while focus is in a text field.
    useEffect(() => {
        const triggers: Array<{ keys: string[]; variant: AlertSoundVariant }> = [
            {
                keys: [
                    'ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown',
                    'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight',
                    'KeyB', 'KeyA',
                ],
                variant: 'imperial-march',
            },
            { keys: ['KeyE', 'KeyV', 'KeyA'], variant: 'cruel-angel' },
            { keys: ['KeyR', 'KeyI', 'KeyC', 'KeyK'], variant: 'rickroll' },
            { keys: ['KeyA', 'KeyO', 'KeyT'], variant: 'aot' },
        ];
        const positions = triggers.map(() => 0);

        const handler = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            if (
                target instanceof HTMLInputElement ||
                target instanceof HTMLTextAreaElement ||
                (target && target.isContentEditable)
            ) {
                return;
            }

            triggers.forEach((trigger, i) => {
                if (e.code === trigger.keys[positions[i]]) {
                    positions[i] += 1;
                    if (positions[i] === trigger.keys.length) {
                        const newlyUnlocked = unlockEasterEgg(trigger.variant);
                        previewAlertSound(trigger.variant);
                        if (newlyUnlocked) {
                            setUnlockedEggs(getUnlockedEasterEggs());
                        }
                        positions[i] = 0;
                    }
                } else {
                    positions[i] = e.code === trigger.keys[0] ? 1 : 0;
                }
            });
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    useEffect(() => {
        if (!autoRefreshMenuOpen) return undefined;
        const onClickOutside = (e: MouseEvent) => {
            if (!autoRefreshMenuRef.current) return;
            if (!autoRefreshMenuRef.current.contains(e.target as Node)) {
                setAutoRefreshMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [autoRefreshMenuOpen]);

    useEffect(() => {
        if (!soundMenuOpen) return undefined;
        const onClickOutside = (e: MouseEvent) => {
            if (!soundMenuRef.current) return;
            if (!soundMenuRef.current.contains(e.target as Node)) {
                setSoundMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [soundMenuOpen]);

    useEffect(() => {
        setCursorStack([]);
    }, [tenantId, eventTypeFilter, statusFilter, dateRange]);

    const serverCurrentPage = cursorStack.length + 1;
    const serverTotalPages = Math.max(1, Math.ceil(totalIncidents / INCIDENTS_PER_PAGE));

    const handleServerPageChange = useCallback((page: number) => {
        if (page === 1) {
            setCursorStack([]);
        } else if (page === serverCurrentPage + 1) {
            const last = incidents.at(-1);
            if (last) setCursorStack((prev) => [...prev, last.startedAt]);
        } else if (page < serverCurrentPage) {
            setCursorStack((prev) => prev.slice(0, page - 1));
        } else if (page === serverTotalPages && hasMore) {
            const last = incidents.at(-1);
            if (last) setCursorStack((prev) => [...prev, last.startedAt]);
        }
    }, [serverCurrentPage, serverTotalPages, incidents, hasMore]);

    const filteredIncidents = useMemo(
        () => incidents.filter((inc) => incidentMatchesSearch(inc, searchTerm)),
        [incidents, searchTerm]
    );

    const hasAnyFilter = Boolean(eventTypeFilter || statusFilter || dateRange.start || searchTerm);

    /** Table skeleton: tab switch (before fetch starts), first load, empty page, or refetch while still empty — not when incidents already shown (same-count refresh). */
    const showHistoryTableSkeleton = useMemo(
        () =>
            historySwitchSkeleton ||
            (historyFetching && (!hasFetched || incidents.length === 0)),
        [historyFetching, hasFetched, incidents.length, historySwitchSkeleton]
    );

    const handleClearFilters = useCallback(() => {
        setHistoryFilterLoadingKey(null);
        setEventTypeFilter('');
        setStatusFilter('');
        setSearchTerm('');
        setDateRange({ start: null, end: null });
        setFilterResetKey((k) => k + 1);
    }, []);

    /* -- Incident columns -- */
    const incidentColumns = useMemo<GenericTableColumn<IncidentGroup>[]>(() => {
        const cols: GenericTableColumn<IncidentGroup>[] = [
            {
                header: 'Date',
                accessor: 'startedAt' as keyof IncidentGroup,
                renderCell: (row: IncidentGroup) => (
                    <span className="text-xs font-mono whitespace-nowrap">
                        {formatTimestamp(row.startedAt)}
                    </span>
                ),
            },
            {
                header: 'Type',
                accessor: 'eventTypes' as keyof IncidentGroup,
                renderCell: (row: IncidentGroup) => {
                    let types = row.eventTypes;
                    if (types.includes('bgp_adjchange') && types.includes('bfd_session')) {
                        types = types.filter((t) => t !== 'bfd_session');
                    }
                    // `traffic_resumed` ("Recovered") is a follow-up that
                    // closes a Data Rate warning, not an alarm category in
                    // its own right. Drop it from the Type column so the
                    // tag list matches the dropdown filter and the
                    // Incident Details modal (where the recovery is
                    // absorbed into the idle's lifecycle).
                    types = types.filter((t) => t !== 'traffic_resumed');
                    // Dedupe by *label* so distinct event types that share a
                    // user-facing name collapse into one tag — e.g.
                    // router_unreachable + router_reachable both show as
                    // "Outage" but represent a single incident on screen.
                    const seen = new Set<string>();
                    const labels: string[] = [];
                    for (const et of types) {
                        const label = getEventTypeLabel(et);
                        if (seen.has(label)) continue;
                        seen.add(label);
                        labels.push(label);
                    }
                    return (
                        <div className="flex flex-wrap gap-1">
                            {labels.map((label) => (
                                <GenericTag
                                    key={label}
                                    label={label}
                                    variant="neutral"
                                    size="small"
                                    rounded="medium"
                                />
                            ))}
                        </div>
                    );
                },
            },
            {
                header: 'Current Status',
                accessor: 'status',
                renderCell: (row: IncidentGroup) => (
                    <GenericTag
                        label={row.status}
                        variant={STATUS_TAG_VARIANT[row.status] || 'neutral'}
                        size="small"
                        rounded="medium"
                    />
                ),
            },
            {
                header: 'Neighbor',
                accessor: 'events' as keyof IncidentGroup,
                renderCell: (row: IncidentGroup) => {
                    const neighbors = getIncidentNeighbors(row);
                    if (neighbors.length === 0) return <span className="text-xs text-semantic-text-secondary">{'\u2014'}</span>;
                    return (
                        <div className="flex flex-col gap-0.5">
                            {neighbors.map((ip) => (
                                <span key={ip} className="text-xs font-mono text-semantic-text-primary whitespace-nowrap">
                                    {ip}
                                </span>
                            ))}
                        </div>
                    );
                },
            },
            {
                header: 'Duration',
                accessor: 'resolvedAt' as keyof IncidentGroup,
                renderCell: (row: IncidentGroup) => {
                    const start = new Date(row.startedAt).getTime();
                    const end = row.resolvedAt
                        ? new Date(row.resolvedAt).getTime()
                        : Date.now();
                    return (
                        <span className="text-xs font-mono text-semantic-text-primary whitespace-nowrap">
                            {formatDuration(Math.max(0, end - start))}
                        </span>
                    );
                },
            },
            {
                header: 'Events',
                accessor: 'eventCount' as keyof IncidentGroup,
                renderCell: (row: IncidentGroup) => (
                    <span className="text-xs text-semantic-text-secondary">
                        {row.eventCount}
                    </span>
                ),
            },
            {
                header: 'Affected Services',
                accessor: 'affectedServices' as keyof IncidentGroup,
                renderCell: (row: IncidentGroup) => {
                    if (!row.affectedServices?.length) {
                        return (
                            <span className="text-xs text-semantic-text-secondary">
                                No match
                            </span>
                        );
                    }
                    return (
                        <div className="flex flex-col gap-1.5">
                            {row.affectedServices.map((svc) => {
                                const detailPath = getServiceDetailPath(
                                    svc.serviceType,
                                    svc.serviceId
                                );
                                const label = svc.serviceName || svc.subscriptionId;
                                const tooltip = buildServiceTooltip(svc);
                                return (
                                    <div
                                        key={svc.serviceType + '-' + String(svc.serviceId)}
                                        className="flex flex-wrap items-center gap-1"
                                        {...viewHref(detailPath)}
                                    >
                                        <GenericTag
                                            label={label}
                                            variant="neutral"
                                            size="small"
                                            rounded="medium"
                                            tooltip={
                                                detailPath
                                                    ? tooltip + ' \u2014 Click to open detail'
                                                    : tooltip
                                            }
                                            onClick={
                                                detailPath
                                                    ? () => handleServiceClick(svc.serviceType, svc.serviceId)
                                                    : undefined
                                            }
                                        />
                                        {svc.tags?.map((tag) => (
                                            <GenericTag
                                                key={svc.serviceType + '-' + String(svc.serviceId) + '-tag-' + tag}
                                                label={tag}
                                                variant="default"
                                                size="small"
                                                rounded="medium"
                                            />
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    );
                },
            },
        ];

        if (isViewingAllCustomers) {
            cols.push({
                header: 'Customer',
                accessor: 'affectedServices' as keyof IncidentGroup,
                renderCell: (row: IncidentGroup) => {
                    const names = Array.from(
                        new Map(
                            row.affectedServices.map((s) => [
                                s.tenantId,
                                s.customerName,
                            ])
                        ).values()
                    );
                    if (!names.length || names.every((n) => !n)) {
                        return (
                            <span className="text-xs text-semantic-text-secondary">
                                {'\u2014'}
                            </span>
                        );
                    }
                    return (
                        <div className="flex flex-col gap-0.5">
                            {names
                                .filter(Boolean)
                                .map((name) => (
                                    <span
                                        key={name}
                                        className="text-xs font-medium text-semantic-text-primary"
                                    >
                                        {name}
                                    </span>
                                ))}
                        </div>
                    );
                },
            });
        }

        cols.push({
            header: 'Details',
            accessor: 'incidentId' as keyof IncidentGroup,
            renderCell: (row: IncidentGroup) => (
                <button
                    className="flex items-center justify-center border border-semantic-border-secondary rounded text-semantic-text-secondary hover:bg-semantic-state-active w-9 h-9"
                    onClick={() => setDetailIncident(row)}
                    aria-label="View incident details"
                >
                    <IconEye className="w-5 h-5" />
                </button>
            ),
        });

        return cols;
    }, [isViewingAllCustomers, handleServiceClick]);

    /* ---------- Render ---------- */

    const currentLastUpdated = activeTab === 'active' ? activeLastUpdated : historyLastUpdated;
    const isCurrentFetching = activeTab === 'active' ? activeIsFetching : historyFetching;

    return (
        <div className="relative">
            {/* Main content card */}
            <GenericCard isCollapsible={false} className="flex max-h-[calc(100vh-200px)] min-h-0 flex-col overflow-hidden">
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {/* Tabs (left) + refresh toolbar (right) */}
                <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-semantic-border-primary px-4 py-3">
                    <div className="flex min-w-0 items-center">
                        <GenericTabs
                            tabs={alertsPageTabs}
                            activeTab={alertsTabValue}
                            onTabChange={handleAlertsTabChange}
                            fullWidth={false}
                            size="sm"
                            className="w-fit max-w-[400px]"
                            aria-label="Alerts view"
                        />
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end sm:gap-3">
                        <div className="order-1 flex items-center gap-1">
                            <button
                                type="button"
                                onClick={handleToggleSoundMuted}
                                aria-pressed={soundMuted}
                                aria-label={soundMuted ? 'Unmute alert sounds' : 'Mute alert sounds'}
                                title={soundMuted ? 'Sounds muted — click to enable' : 'Sounds enabled — click to mute'}
                                className={`inline-flex h-8 w-8 items-center justify-center rounded-md border border-semantic-border-secondary bg-semantic-bg-primary transition-colors hover:bg-semantic-state-active ${
                                    soundMuted
                                        ? 'text-semantic-text-danger'
                                        : 'text-semantic-text-primary'
                                }`}
                            >
                                {soundMuted ? (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                        <path d="M11 5L6 9H2v6h4l5 4V5z" />
                                        <line x1="23" y1="9" x2="17" y2="15" />
                                        <line x1="17" y1="9" x2="23" y2="15" />
                                    </svg>
                                ) : (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                        <path d="M11 5L6 9H2v6h4l5 4V5z" />
                                        <path d="M15.54 8.46a5 5 0 010 7.07" />
                                        <path d="M19.07 4.93a10 10 0 010 14.14" />
                                    </svg>
                                )}
                            </button>
                            <div ref={soundMenuRef} className="relative inline-flex items-stretch">
                                <GenericButton
                                    variant="secondary"
                                    size="sm"
                                    label="Sound"
                                    onClick={() => previewAlertSound(soundVariant)}
                                    aria-label="Preview the selected notification sound"
                                    title="Click to preview the selected sound"
                                    className="rounded-r-none border-r-0"
                                />
                                <button
                                    type="button"
                                    onClick={() => setSoundMenuOpen((v) => !v)}
                                    aria-label="Pick notification sound"
                                    aria-haspopup="menu"
                                    aria-expanded={soundMenuOpen}
                                    className="relative inline-flex items-center justify-center w-7 rounded-r-sm border border-semantic-border-secondary bg-semantic-bg-primary text-semantic-text-primary hover:bg-semantic-bg-secondary shadow-sm"
                                >
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                                        <path d="M2 4 L5 7 L8 4" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                {soundMenuOpen && (
                                    <div
                                        role="menu"
                                        className="absolute left-full top-0 z-50 ml-2 w-56 rounded-md border border-semantic-border-secondary bg-semantic-bg-primary py-1 shadow-lg"
                                    >
                                        {ALERT_SOUND_OPTIONS.map((opt) => {
                                            const isSelected = soundVariant === opt.variant;
                                            return (
                                                <button
                                                    key={opt.variant}
                                                    type="button"
                                                    role="menuitemradio"
                                                    aria-checked={isSelected}
                                                    onClick={() => handleSelectSoundVariant(opt.variant)}
                                                    title={opt.description}
                                                    className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-xs hover:bg-semantic-state-active ${
                                                        isSelected
                                                            ? 'bg-semantic-bg-brand-secondary font-semibold text-semantic-text-brand'
                                                            : 'text-semantic-text-primary'
                                                    }`}
                                                >
                                                    <span>{opt.label}</span>
                                                    {isSelected && (
                                                        <span
                                                            aria-hidden
                                                            className="ml-2 inline-block h-1.5 w-1.5 rounded-full bg-current"
                                                        />
                                                    )}
                                                </button>
                                            );
                                        })}
                                        {unlockedEggs.length > 0 && (
                                            <>
                                                <div className="my-1 border-t border-semantic-border-secondary" />
                                                <div className="px-3 pt-1 pb-0.5 text-[9px] font-semibold uppercase tracking-wide text-semantic-text-secondary">
                                                    Unlocked
                                                </div>
                                                {unlockedEggs.map((opt) => {
                                                    const isSelected = soundVariant === opt.variant;
                                                    return (
                                                        <button
                                                            key={opt.variant}
                                                            type="button"
                                                            role="menuitemradio"
                                                            aria-checked={isSelected}
                                                            onClick={() => handleSelectSoundVariant(opt.variant)}
                                                            title={opt.description}
                                                            className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-xs hover:bg-semantic-state-active ${
                                                                isSelected
                                                                    ? 'bg-semantic-bg-brand-secondary font-semibold text-semantic-text-brand'
                                                                    : 'text-semantic-text-primary'
                                                            }`}
                                                        >
                                                            <span>{opt.label}</span>
                                                            {isSelected && (
                                                                <span
                                                                    aria-hidden
                                                                    className="ml-2 inline-block h-1.5 w-1.5 rounded-full bg-current"
                                                                />
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <span
                            style={{
                                transitionProperty: 'background-color, border-color, color',
                                /* Enter success instantly; fade out to neutral over UPDATED_LABEL_TRANSITION_MS */
                                transitionDuration: updatedLabelFlash
                                    ? '0ms'
                                    : `${UPDATED_LABEL_TRANSITION_MS}ms`,
                                transitionTimingFunction: 'ease-out',
                            }}
                            className={`order-3 inline-flex w-full items-center whitespace-nowrap rounded-md border px-2 py-1 text-xs sm:order-2 sm:w-auto ${
                                updatedLabelFlash
                                    ? 'border-semantic-border-success-primary bg-semantic-bg-success-secondary text-semantic-text-success'
                                    : 'border-transparent bg-transparent text-semantic-text-secondary'
                            }`}
                        >
                            Updated {formatClockTime(currentLastUpdated)}
                        </span>
                        <div ref={autoRefreshMenuRef} className="relative order-2 inline-flex items-stretch sm:order-3">
                            <GenericButton
                                variant="secondary"
                                size="sm"
                                iconLeftName="refresh"
                                label="Refresh"
                                onClick={handleManualRefresh}
                                loading={isCurrentFetching}
                                aria-label="Refresh now"
                                className="rounded-r-none border-r-0"
                            />
                            <button
                                type="button"
                                onClick={() => setAutoRefreshMenuOpen((v) => !v)}
                                aria-label="Refresh options"
                                aria-haspopup="true"
                                aria-expanded={autoRefreshMenuOpen}
                                className="relative inline-flex items-center justify-center w-7 rounded-r-sm border border-semantic-border-secondary bg-semantic-bg-primary text-semantic-text-primary hover:bg-semantic-bg-secondary shadow-sm"
                            >
                                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                                    <path d="M2 4 L5 7 L8 4" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                {autoRefresh && (
                                    <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-semantic-bg-success-primary" aria-hidden="true" />
                                )}
                            </button>
                            {autoRefreshMenuOpen && (
                                <div
                                    role="menu"
                                    className="absolute right-0 top-full mt-1 z-50 min-w-[240px] rounded-md border border-semantic-border-primary bg-semantic-bg-primary shadow-lg p-3"
                                >
                                    <div className="flex items-center justify-between gap-3 text-xs text-semantic-text-primary">
                                        <span>Auto-refresh every 1 minute</span>
                                        <GenericSwitch checked={autoRefresh} onChange={handleAutoRefreshChange} />
                                    </div>
                                    <p className="mt-2 text-[11px] leading-snug text-semantic-text-secondary">
                                        Pauses automatically when the tab is in the background.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sits above both tabs: the pause applies to the feed, not to
                    one view of it. */}
                <AlertingPausedNotice />

                {activeTab === 'active' && (
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        <ActiveAlarmsSection
                            tenantId={tenantId}
                            autoRefresh={autoRefresh}
                            onLastUpdatedChange={setActiveLastUpdated}
                            onActiveEventTotalChange={setActiveAlarmsEventTotal}
                            onSuccessfulRefresh={triggerUpdatedLabelFlash}
                            onFetchingChange={setActiveIsFetching}
                            refreshNonce={activeRefreshNonce}
                            syncAlertsMenuNetworkEventsCount={syncAlertsMenuNetworkEventsCount}
                        />
                    </div>
                )}

                {activeTab === 'history' && (
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        {fetchError ? (
                            <ErrorFetchingItems text={fetchError} />
                        ) : (
                            <>
                                {!(
                                    hasFetched &&
                                    incidents.length === 0 &&
                                    !hasAnyFilter &&
                                    !showHistoryTableSkeleton
                                ) && (
                                    <>
                                        {/* Filters toolbar */}
                                        <div className="relative z-30 flex flex-wrap items-end gap-3 overflow-visible bg-semantic-bg-primary px-4 pb-3 pt-4">
                                            <GenericDropdown
                                                items={EVENT_TYPE_OPTIONS}
                                                value={EVENT_TYPE_LABEL_MAP[eventTypeFilter] || 'All Events'}
                                                onSelect={(label) => {
                                                    setHistoryFilterLoadingKey('eventType');
                                                    setEventTypeFilter(EVENT_TYPE_VALUE_MAP[label] ?? '');
                                                }}
                                                size="sm"
                                                widthClass="w-auto"
                                                minWidthClass="min-w-[120px]"
                                                maxWidthClass="max-w-[160px]"
                                                disabled={historyFetching}
                                                loading={
                                                    historyFetching &&
                                                    historyFilterLoadingKey === 'eventType'
                                                }
                                                fixedPosition
                                            />
                                            <GenericDropdown
                                                items={STATUS_OPTIONS}
                                                value={STATUS_LABEL_MAP[statusFilter] || 'All Statuses'}
                                                onSelect={(label) => {
                                                    setHistoryFilterLoadingKey('status');
                                                    setStatusFilter(STATUS_VALUE_MAP[label] ?? '');
                                                }}
                                                size="sm"
                                                widthClass="w-auto"
                                                minWidthClass="min-w-[120px]"
                                                maxWidthClass="max-w-[180px]"
                                                disabled={historyFetching}
                                                loading={
                                                    historyFetching &&
                                                    historyFilterLoadingKey === 'status'
                                                }
                                                fixedPosition
                                            />
                                            <GenericDateTimeRangePicker
                                                label=""
                                                value={dateRange}
                                                onChange={(val) => {
                                                    setHistoryFilterLoadingKey('dateRange');
                                                    setDateRange(val);
                                                }}
                                                maxDate={new Date()}
                                                dateOnly
                                                className="w-[220px]"
                                                disabled={historyFetching}
                                            />
                                            <div className="min-w-[180px] max-w-[320px] flex-1">
                                                <GenericSearchbar
                                                    key={filterResetKey}
                                                    placeholder="Search neighbor, service, customer..."
                                                    size="xs"
                                                    onFiltersChange={Function.prototype as () => void}
                                                    serverSideSearch
                                                    onServerSideSearchChange={(val) => setSearchTerm(val)}
                                                    serverSideDebounceMs={300}
                                                    inputMaxWidth="100%"
                                                />
                                            </div>

                                            <span className="ml-auto text-xs text-semantic-text-secondary">
                                                {totalIncidents} incident{totalIncidents === 1 ? '' : 's'}
                                            </span>
                                        </div>
                                        {hasAnyFilter && (
                                            <div className="px-4 pb-2">
                                                <GenericButton
                                                    variant="outlined"
                                                    size="xsm"
                                                    onClick={handleClearFilters}
                                                    label="Clear filters"
                                                />
                                            </div>
                                        )}
                                    </>
                                )}

                                {!showHistoryTableSkeleton &&
                                hasFetched &&
                                incidents.length === 0 &&
                                !hasAnyFilter ? (
                                    <EmptyItems />
                                ) : !showHistoryTableSkeleton &&
                                  hasFetched &&
                                  filteredIncidents.length === 0 &&
                                  (incidents.length > 0 || hasAnyFilter) ? (
                                    <div className="flex items-center justify-center py-12 text-sm text-semantic-text-secondary">
                                        No events match the current filters.
                                    </div>
                                ) : (
                                    <GenericCard
                                        hasBorder={false}
                                        isCollapsible={false}
                                        className="flex min-h-0 w-full min-w-0 flex-1 flex-col border-0 px-4 pb-4 shadow-none mt-4"
                                    >
                                        <GenericTable<IncidentGroup>
                                            columns={incidentColumns}
                                            data={filteredIncidents}
                                            rowKey={(row) => row.incidentId}
                                            serverSide
                                            totalCount={totalIncidents}
                                            currentPage={serverCurrentPage}
                                            rowsPerPage={INCIDENTS_PER_PAGE}
                                            onPageChange={handleServerPageChange}
                                            loading={showHistoryTableSkeleton}
                                            loadingSkeletonRows={NETWORK_ALERTS_TABLE_SKELETON_ROWS}
                                            loadingSkeletonCols={NETWORK_ALERTS_TABLE_SKELETON_COLS}
                                            width="100%"
                                            tableClassName="bg-semantic-bg-primary"
                                            maxTableBodyHeight="100%"
                                        />
                                    </GenericCard>
                                )}
                            </>
                        )}
                    </div>
                )}
                </div>
            </GenericCard>

            <IncidentDetailModal
                incident={detailIncident}
                onClose={() => setDetailIncident(null)}
            />
        </div>
    );
};

export default NetworkAlertsPage;
